import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import _ from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { viewModelToBulkModelForConversionGoals } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { ERROR_ENTITY, isBulkWrappedResponse, getBulkUploadErrorType, modelToViewModelForBulkErrors, removeReferencedEntityInvalidIdErrors } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { getErrorsPlaceholders } from '@bingads-webui-campaign-react/xandr-campaign';
import { isAudienceShoppingCampaign } from '@bingads-webui-campaign-react/constants';
import { bulkModelToViewModelForAdGroup } from './ad-group';
import { bulkModelToViewModelForCampaign, viewModelToModelForCampaign } from './campaign';
import { PLACEHOLDER_ID } from '../constants';
import { viewModelToBulkModelForAudiences, viewModelToBulkModelForTargeting, viewModelToBulkModelForLocationTargets } from './campaign-criterions';
import { viewModelToBulkModelForAdGroup } from './ad-group-bulk';

const campaignEDM = edmCampaign.types.resolve('Model.ShoppingCampaign');
const campaignResource = new campaignEDM.ResourceIdentifier();

const createOrUpdateCampaign = (data) => {
  const createOrUpdateCall = data && data.Id ?
    campaignResource.bulkUpdate.bind(campaignResource) :
    campaignResource.bulkCreate.bind(campaignResource);
  return createOrUpdateCall(data);
};

const GENERIC_ERROR_MESSAGE_KEY = _TL_("We've encountered an error and can't continue. Please contact support for further assistance.");

const bulkUpsertCampaignSettingsEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
};

export const saveCampaign = ({
  campaign: campaignViewModel,
  adGroup: adGroupViewModel,
  viewModelToBulkModelForAdSchedules = null,
  updateCampaignStatus = true,
  bundleLocationCriterions,
  bundleCountries,
}, { scenario, i18n, activityLogger }) => {
  const campaignModel = viewModelToModelForCampaign(campaignViewModel);
  const isAudienceCampaign = isAudienceShoppingCampaign(campaignViewModel.data.CampaignType, campaignViewModel.data.CampaignSubType);

  let conversionGoalsAssociations = {};
  let adSchedulesModel = [];

  const modelToSave = {
    ...campaignModel,
    ...conversionGoalsAssociations,
  };

  if (!updateCampaignStatus) {
    delete modelToSave.Status;
  }

  if (!_.isEmpty(campaignViewModel.data.conversionGoals)) {
    conversionGoalsAssociations = {
      CampaignConversionGoalAssociations: viewModelToBulkModelForConversionGoals(campaignViewModel, campaignViewModel.data.Id || PLACEHOLDER_ID.CampaignNegativeReferenceId),
    };
    _.extend(modelToSave, { ...conversionGoalsAssociations });
  }

  if (viewModelToBulkModelForAdSchedules) {
    adSchedulesModel = viewModelToBulkModelForAdSchedules({ campaignViewModel });
  }
  const targetingModel = viewModelToBulkModelForTargeting({ campaignViewModel });
  const locationModel = viewModelToBulkModelForLocationTargets({ campaignViewModel, bundleLocationCriterions });

  modelToSave.CampaignCriterions = [
    ...adSchedulesModel,
    ...targetingModel,
    ...locationModel,
  ];

  modelToSave.CampaignAudienceCriterions = viewModelToBulkModelForAudiences({ campaignViewModel });


  if (!modelToSave.Id) {
    modelToSave.Id = PLACEHOLDER_ID.CampaignNegativeReferenceId;
  }

  if (adGroupViewModel) {
    modelToSave.AdGroups = [
      viewModelToBulkModelForAdGroup({
        adGroupViewModel,
        campaignViewModel: campaignViewModel.data,
        i18n,
        saveAdGroupCriterions: isAudienceCampaign, // For AWF, targets are created at ad group level
      }),
    ];
  }

  return createOrUpdateCampaign(modelToSave)
    // with bulk the result will have shape of:
    // - { ...entityData } - in case of success;
    // - { entity: { ...entityData }, errors: [(EditorialError|MTError)] } - in case of error(s).
    .then((result) => {
      const data = {
        campaign: bulkModelToViewModelForCampaign({
          campaignModel: result,
          originalCampaignModel: campaignViewModel,
          bundleCountries,
        }),
        ...getErrorsPlaceholders(),
      };

      if (adGroupViewModel) {
        data.adGroup = bulkModelToViewModelForAdGroup({
          adGroupModel: result.AdGroups[0],
          originalAdGroupViewModel: adGroupViewModel,
          bundleCountries,
          i18n,
        });
      }

      return data;
    }, (bulkOrSystemError) => {
      if (isBulkWrappedResponse(bulkOrSystemError)) {
        const { entity, errors } = bulkOrSystemError;
        const campaign = entity ?
          bulkModelToViewModelForCampaign({
            campaignModel: entity,
            originalCampaignModel: campaignViewModel,
          }) :
          campaignViewModel;

        const adGroupData = {};
        if (adGroupViewModel) {
          adGroupData.adGroup = bulkModelToViewModelForAdGroup({
            adGroupModel: entity.AdGroups[0],
            originalAdGroupViewModel: adGroupViewModel,
            i18n,
          });
        }

        let filteredErrors = errors;
        if (campaign.data.Id <= 0) {
          filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
        }

        const serverErrors = modelToViewModelForBulkErrors(
          filteredErrors,
          bulkUpsertCampaignSettingsEntityErrorMap,
          scenario,
          i18n,
          activityLogger
        );

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          campaign,
          serverErrors,
          ...adGroupData,
        });
      }

      // Activity
      // eslint-disable-next-line no-console
      console.warn(`Campaign bulk upsert system error: ${bulkOrSystemError.stack || bulkOrSystemError.message || bulkOrSystemError}`);
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        campaign: campaignViewModel,
        systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
        // TODO: We would want to return the criterions with Ids, but UI's bulk drops them
      });
    });
};
